/* Gleva · Direction B · gvb-pdp.js — the product buy box (BUILD-SPEC §2.3, §5, §6.3, §7). ES module, loaded by
   snippets/gvb-main-product.liquid. One source of truth: the chosen variant (Impact `variant:change`) or the chosen
   Kaching tier drives, instantly: the title price + per-label, the Add-to-cart label and price, Buy now (Fastrr) for
   single units only, the free-delivery strip, the sticky bar and the gallery position.
   Events: listens `variant:change` (Impact), `variant:add`, `gvb:pick-tier` {title} and `gvb:select-variant`
   {productId, variantId} (both cancelable; handled → preventDefault); fires `gvb:tier-change` {title, price, express,
   tier} on document. API window.GVB.pdp = { state, selectTier(titleOrIndex), selectVariant(id) }. Prices are paise.
   Sold out: seeded from the server-rendered button (.is-soldout) and the shades JSON, so a sold-out product never gets a
   live Add to cart / Buy now (also not through a Kaching tier). A link to a sold-out variant of a product that can still
   be bought (?variant=<id>, old ads) lands on the first available value instead (fixStep).
   Ad links: ?tier=<n|slug> (1 = first tier; "kit", "duo", "buy-2"… match the tier name or Kaching title; "kit" falls
   back to the tier marked "Select for #tiers links") and #tiers / #tiers-<slug> (prefix match) select a tier on load.
   Bundle mode: only once Kaching has drawn a deal bar here that a gvb_tier block is set up for (or any bar, on a page
   without gvb_tier blocks) does the buy-box root get .bb--bundle; everything that exists only for that deal hangs off
   the class (gvb-kaching.css): the hidden variant picker (gvb_kaching "Hide the variant picker"; .bb--kpick brings it
   back for a tier that offers no choice of the page product), the hidden full-price cross-sell cards ("Hide these
   cross-sell products"), the legend. No deal (switched off, slow, blocked) or another deal = no class = the page as
   without the block. Kaching drawing late (any time after load) still switches the page over. Kaching's checkbox add-ons are not
   tiers: they are drawn as tick rows under the tiers and their ticked prices are added to Add to cart and the sticky
   bar (an add-on also hides Buy now, which would drop it). */
import { G } from 'gvb';

const $ = (s, r) => (r || document).querySelector(s);
const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim().toLowerCase();
const esc = (t) => String(t == null ? '' : t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
/* "Rs. 1,497.00" / "₹1,497" → 149700 */
const parsePrice = (t) => {
  t = String(t || '');
  const i = t.search(/\d/);
  if (i < 0) return null;
  const n = parseFloat(t.slice(i).replace(/,/g, '').match(/^[\d.]+/)[0]);
  return isNaN(n) ? null : Math.round(n * 100);
};

function boot() { $$('[data-gvb-pdp]').forEach((r) => { if (!r._gvb) { r._gvb = true; init(r); } }); }

function init(root) {
  let cfg = {};
  try { cfg = JSON.parse($('script[data-gvb-pdp-config]')?.textContent || '{}'); } catch (e) { cfg = {}; }
  const shades = cfg.shades || {};
  const tiers = $$('[data-gvb-tier-config] template[data-tier]').map((t) => ({
    el: t, index: Number(t.dataset.index), title: t.dataset.title || '', name: t.dataset.name || '', sub: t.dataset.sub || '',
    subLong: t.dataset.subLong || '', per: t.dataset.per || '', tag: t.dataset.tag || '', express: t.dataset.express === 'true',
    cta: t.dataset.cta || '', hash: t.dataset.hash === 'true', pre: t.dataset.pre === 'true', shadeThumbs: t.dataset.shadeThumbs === 'true', unit: t.dataset.unit || '', n: Number(t.dataset.n) || 0,
    was: Math.round((Number(t.dataset.was) || 0) * 100), wasUnits: Number(t.dataset.wasUnits) || 0, labels: (t.dataset.labels || '').split('|').map((x) => x.trim()),
    variantId: t.dataset.variantId || '', variantOut: t.dataset.variantOut === 'true'
  }));
  const form = () => document.forms[root.dataset.formId];
  const sticky = $('[data-gvb-sticky]');
  const state = { kind: 'variant', name: '', variant: '', price: null, add: 0, per: '', express: true, soldout: false, cta: '', thumbs: '', tier: -1, kb: null };
  /* bundle-mode config from the gvb_kaching block (gvb-main-product prints it on the root) */
  const hidePicker = root.dataset.kPicker === 'hide';
  const hideCards = (root.dataset.kHide || '').split(',').map((h) => h.trim().toLowerCase()).filter(Boolean);
  /* what the page shows without a deal, to go back to if Kaching's bars disappear again */
  const base = { label: $('[data-atc-label]', root)?.textContent || 'Add to cart', price: parsePrice($('[data-atc-price]', root)?.textContent), name: sticky ? ($('[data-sticky-name]', sticky)?.textContent || '') : '', thumbs: sticky ? ($('[data-sticky-thumbs]', sticky)?.innerHTML || '') : '' };
  /* sold-out seeds: the server renders the chosen variant's state on the button (buy-buttons.liquid: .is-soldout +
     disabled). varOut = the chosen native variant is sold out; productOut = no variant can be bought at all */
  const atc0 = $('[data-atc]', root);
  let varOut = !!(atc0 && atc0.classList.contains('is-soldout'));
  const shadeList = Object.keys(shades).map((k) => shades[k]);
  const productOut = varOut
    && (shadeList.length ? shadeList.every((s) => s.available === false) : true)
    && !$$('input[data-option-position]', root).some((x) => !x.hasAttribute('data-soldout'));
  state.soldout = varOut;
  let fixLeft = 0; /* steps left in the sold-out landing fix (fixStep) */

  /* ---------------------------------------------------------------- render state everywhere, instantly */
  function paint() {
    const p = state.price;
    /* Add to cart, the sticky bar and the free-delivery strip carry what goes in the bag: the tier plus ticked add-ons */
    const t = p != null ? p + (state.add || 0) : null;
    if (p != null) {
      G.setPrice($$('[data-bb-price]', root), p);
      /* keep the empty .gvb-pa first child: Kaching rewrites any button element whose first text has digits */
      $$('[data-atc-price]', root).forEach((el) => { const m = G.money(t); if (el.textContent !== m || !el.firstElementChild) el.innerHTML = '<span class="gvb-pa"></span>' + m; });
      G.updateAssurance(t, root);
    }
    $$('[data-bb-per]', root).forEach((el) => { el.textContent = state.per; el.hidden = !state.per; });
    if (state.kind === 'tier') $$('[data-atc-label]', root).forEach((el) => { el.textContent = state.cta || 'Add to cart'; });
    $$('[data-atc]', root).forEach((b) => { b.disabled = !!state.soldout; b.classList.toggle('is-soldout', !!state.soldout); });
    const row = $('buy-buttons[data-cta-row]', root);
    if (row) {
      const kit = state.kind === 'tier' && !state.express;
      row.classList.toggle('is-kit', kit);
      row.classList.toggle('is-out', !!state.soldout);
      const hideExpress = kit || state.soldout;
      $$('[data-gvb-express]', row).forEach((x) => {
        x.setAttribute('aria-hidden', hideExpress ? 'true' : 'false');
        $$('button', x).forEach((b) => { b.tabIndex = hideExpress ? -1 : 0; });
      });
      $$('.shopify-payment-button', row).forEach((x) => { x.toggleAttribute('inert', hideExpress); });
    }
    if (sticky) {
      const n = $('[data-sticky-name]', sticky); if (n && state.name) n.textContent = state.name;
      if (p != null) G.setPrice($$('[data-sticky-price]', sticky), t);
      const per = $('[data-sticky-per]', sticky); if (per) { per.textContent = state.per; per.hidden = !state.per; }
      const th = $('[data-sticky-thumbs]', sticky); if (th && state.thumbs && th._k !== state.thumbs) { th.innerHTML = state.thumbs; th._k = state.thumbs; }
      const b = $('[data-sticky-atc]', sticky);
      if (b) { b.disabled = !!state.soldout; b.classList.toggle('is-soldout', !!state.soldout); }
    }
  }

  /* ---------------------------------------------------------------- native variants (Impact <variant-picker>) */
  const pickerThumbs = (label) => {
    const t = label && $('[data-thumbs], .tier-thumbs', label);
    return t ? '<span class="tier-thumbs ' + (t.className.match(/n\d/) || ['n1'])[0] + '">' + t.innerHTML + '</span>' : '';
  };
  function fromInput(inp, optimistic) {
    if (!inp) return;
    const label = inp.closest('label');
    if (optimistic) {
      const fs = inp.closest('fieldset') || inp.closest('variant-picker');
      $$('input[data-option-position]', fs).forEach((x) => { const l = x.closest('label'); if (l) l.classList.toggle('is-on', x.checked); });
      const nm = inp.dataset.name || '';
      $$('[data-shade-label]', fs).forEach((el) => { el.textContent = nm; });
      const hint = $('[data-shade-hint]', fs);
      if (hint) hint.innerHTML = inp.dataset.hint ? '<b>' + esc(nm) + ':</b> ' + esc(inp.dataset.hint) : '';
    }
    if (state.kind === 'tier') return; /* a Kaching tier owns price and labels */
    if (inp.dataset.price) state.price = Number(inp.dataset.price);
    state.per = inp.dataset.per || '';
    state.name = inp.dataset.name || state.name;
    varOut = inp.hasAttribute('data-soldout');
    state.soldout = productOut || varOut;
    const th = pickerThumbs(label); if (th) state.thumbs = th;
    paint();
  }
  document.addEventListener('change', (e) => {
    const inp = e.target;
    if (inp && inp.matches && inp.matches('input[data-option-position]') && root.contains(inp)) {
      if (e.isTrusted) fixLeft = 0; /* the shopper's own pick ends the sold-out landing fix (fixStep) */
      fromInput(inp, true);
    }
  });
  root.addEventListener('variant:change', (e) => {
    const v = e.detail && e.detail.variant;
    if (!v) return;
    const auto = fixLeft > 0;
    const inp = $$('input[data-option-position]:checked', root)[0];
    varOut = !v.available;
    if (state.kind !== 'tier') {
      state.price = v.price;
      state.soldout = productOut || varOut;
      state.name = (inp && inp.dataset.name) || v.title || state.name;
      state.per = (inp && inp.dataset.per) || '';
      const th = pickerThumbs(inp && inp.closest('label')); if (th) state.thumbs = th;
    }
    else state.soldout = kaching.tierOut();
    paint();
    if (!auto) galleryTo(v); /* a corrected landing keeps the gallery where a plain link opens it */
    if (state.kb) requestAnimationFrame(() => kaching.decorate());
    if (auto && (v.available || --fixLeft < 1 || !fixStep())) fixLeft = 0;
  });

  /* ---------------------------------------------------------------- ?variant=<sold-out id> (old ads and links)
     While another variant can be bought, a link that selects a sold-out one lands as if an available one were chosen:
     the first available value is picked through the picker's own change path (Impact fetches the section, swaps the
     blocks, so a sold-out value the picker hides is gone again, and rewrites ?variant= with history.replaceState).
     Deepest option first (the metallic lipstick keeps its "Pack of 1"); on load only, at most one step per option,
     ended by any pick of the shopper's own. A sold-out product stays as it is. No reload. */
  function fixStep() {
    const ins = $$('input[data-option-position]', root);
    const pos = Array.from(new Set(ins.map((x) => x.dataset.optionPosition))).sort((a, b) => b - a);
    for (const p of pos) {
      const group = ins.filter((x) => x.dataset.optionPosition === p);
      const cur = group.find((x) => x.checked);
      const to = group.find((x) => !x.hasAttribute('data-soldout') && !x.hasAttribute('data-product-url'));
      if (!cur || !to || !cur.hasAttribute('data-soldout')) continue;
      /* no sold-out flash while the section loads: drop the "· Sold out" marks now, and hide the old value when the
         picker hides sold-out values (the shades JSON then leaves it out; single option only, it is keyed by option 1) */
      $$('.sw-out', cur.closest('variant-picker') || root).forEach((x) => x.remove());
      const l = cur.closest('label');
      if (l && pos.length === 1 && shadeList.length && !shadeList.some((s) => String(s.id) === cur.dataset.variantId)) {
        l.style.display = 'none';
        const grid = l.closest('.shade-grid');
        if (grid) grid.style.setProperty('--cols', Math.max(1, Math.min(3, $$('label', grid).filter((x) => x.style.display !== 'none').length)));
      }
      /* Add to cart is live from here on: the form must already carry the new variant, never the sold-out one */
      const f = form();
      if (f && f.elements.id && to.dataset.variantId) f.elements.id.value = to.dataset.variantId;
      to.checked = true;
      to.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  /* ---------------------------------------------------------------- gallery follows the variant (phone rail) */
  function galleryTo(v) {
    const rail = $('[data-gvb-gal] [data-rail]', root);
    if (!rail || window.matchMedia('(min-width:1024px)').matches) return;
    let slide = v.featured_media ? $('.gal-slide[data-media-id="' + v.featured_media.id + '"]', rail) : null;
    if (!slide) slide = $$('.gal-slide[data-variant]', rail).find((s) => s.dataset.variant === norm(v.title)) || null;
    if (!slide) return;
    const pad = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
    rail.scrollTo({ left: slide.offsetLeft - pad, behavior: G.reduce ? 'auto' : 'smooth' });
  }

  /* ---------------------------------------------------------------- Kaching tiers (BUILD-SPEC §6.3) */
  const kaching = {
    mo: null,
    on: false,
    /* deal bars only: Kaching's checkbox add-ons also carry .kaching-bundles__bar, but they are never tiers */
    bars(kb) { return kb ? $$('.kaching-bundles__bar', kb).filter((b) => !b.closest('.kaching-bundles__checkbox-upsells')) : []; },
    addons(kb) { return kb ? $$('.kaching-bundles__checkbox-upsells__upsell', kb) : []; },
    current() { const bars = this.bars(state.kb); return bars.find((b) => b.classList.contains('kaching-bundles__bar--selected')) || null; },
    cfgFor(bar, i) {
      const title = norm($('.kaching-bundles__bar-title', bar)?.textContent);
      return tiers.find((t) => t.title && t.title === title) || tiers.find((t) => !t.title && t.index === i) || null;
    },
    shadeOf(val) { return shades[val] || { name: val, c: 'var(--cream)', available: true }; },
    dots(vals, max) {
      const list = vals.slice(0, max);
      let h = list.map((v) => '<span class="tth tth-dot" style="--c:' + esc(this.shadeOf(v).c) + '"></span>').join('');
      if (vals.length > max) h += '<span class="tth tth-more">+' + (vals.length - max) + '</span>';
      const n = list.length + (vals.length > max ? 1 : 0);
      return '<span class="tier-thumbs n' + Math.max(1, n) + '" aria-hidden="true">' + h + '</span>';
    },
    /* the deal as Kaching printed it (element API first, else its JSON in <head>): maps bundle items to products */
    deal() {
      if (this._deal) return this._deal; /* not cached while Kaching has not printed it yet */
      let d = null;
      try { const blk = state.kb && $('kaching-bundles-block', state.kb); d = blk && typeof blk.deal === 'function' ? blk.deal() : null; } catch (e) { d = null; }
      if (!d || !d.dealBars) {
        try { d = JSON.parse($('script.kaching-bundles-deal-block-settings[data-product-id="' + cfg.productId + '"]')?.textContent || 'null'); } catch (e) { d = null; }
      }
      this._deal = d && d.dealBars ? d : null;
      return this._deal;
    },
    dbar(bar) { const dl = this.deal(); return dl && bar ? dl.dealBars.find((x) => x.id === bar.dataset.dealBarId) || null : null; },
    /* Kaching's product JSON (the page product and every product of the deal; prices in paise) */
    prod(id) {
      this._p = this._p || {};
      if (!(id in this._p)) { let p = null; try { p = JSON.parse($('script.kaching-bundles-product[data-product-id="' + id + '"]')?.textContent || 'null'); } catch (e) { p = null; } this._p[id] = p; }
      return this._p[id];
    },
    /* a product's lowest price today (among the item's locked variants if any; buyable ones first), null = unknown */
    low(id, vids) {
      const p = this.prod(id);
      let vs = p && p.variants ? p.variants : [];
      if (vids && vids.length) vs = vs.filter((v) => vids.indexOf(String(v.id)) > -1);
      const ok = vs.filter((v) => v.availableForSale !== false);
      const ps = (ok.length ? ok : vs).map((v) => Number(v.price)).filter((x) => isFinite(x) && x > 0);
      return ps.length ? Math.min.apply(null, ps) : null;
    },
    /* the tier bought separately at today's prices (Kaching's product JSON + the deal bar): quantity breaks and buy X
       get Y = units × the page product, bundles = Σ items × quantity, "Separate price in units" = that many of the page
       product's cheapest unit (Pack of 2). null = not known, so no saving is shown */
    sep(bar, c) {
      const db = this.dbar(bar), page = cfg.productId;
      if (!db) return null;
      if (c && c.wasUnits) { const u = this.low(page); return u == null ? null : u * c.wasUnits; }
      if (db.dealBarType === 'quantity-break' || db.dealBarType === 'bxgy') {
        const q = this.qty(db), u = this.low(page);
        return u == null || !q ? null : u * q;
      }
      if (db.dealBarType !== 'bundle' || !db.bundleProducts || !db.bundleProducts.length) return null;
      let sum = 0;
      for (const bp of db.bundleProducts) {
        const u = this.low(bp.productGID === 'default' ? page : String(bp.productGID).split('/').pop(), (bp.variantGIDs || []).map((g) => String(g).split('/').pop()));
        if (u == null) return null;
        sum += u * (Number(bp.quantity) || 1);
      }
      return sum;
    },
    qty(db) {
      if (!db) return 0;
      if (db.dealBarType === 'bxgy') return (Number(db.buyQuantity) || 0) + (Number(db.getQuantity) || 0);
      if (db.dealBarType === 'bundle') return (db.bundleProducts || []).reduce((s, bp) => s + (Number(bp.quantity) || 1), 0);
      return Number(db.quantity) || 0;
    },
    /* Kaching's "show prices per item" / "show both prices": in a bar of the page product only, its first price is per item */
    perItem(db) {
      const dl = this.deal();
      if (!dl || !(dl.showPricesPerItem || dl.showBothPrices) || !db) return false;
      return db.dealBarType === 'quantity-break' || db.dealBarType === 'bxgy' || (db.dealBarType === 'bundle' && (db.bundleProducts || []).every((bp) => bp.productGID === 'default'));
    },
    /* a bar's variant selects, each with its bundle item (n, Kaching item order) and whether it is the page product
       (own). Selling-plan, per-bar upsell, gift and add-on selects are not picks of the tier. Unit selects (quantity
       breaks, buy X get Y) are always the page product. A bundle item is mapped by order to the deal bar's
       bundleProducts[n] ("default" = the page product); without the deal data, an item that links to its product page
       is another product (Kaching links every item except the page product) */
    units(bar) {
      const deal = this.deal();
      const dbar = deal ? deal.dealBars.find((x) => x.id === bar.dataset.dealBarId) : null;
      const items = $$('.kaching-bundles__bundle-products__product', bar).filter((x) => !x.classList.contains('kaching-bundles__bundle-products__product--placeholder'));
      return $$('select.kaching-bundles__bar-variant-select', bar)
        .filter((s) => !s.classList.contains('kaching-bundles__selling-plan-select') && !s.closest('.kaching-bundles__upsell, .kaching-bundles__free-gift, .kaching-bundles__checkbox-upsells'))
        .map((sel) => {
          const it = sel.closest('.kaching-bundles__bundle-products__product');
          const n = it ? items.indexOf(it) : -1;
          let own = true;
          if (it) {
            const bp = dbar && dbar.bundleProducts ? dbar.bundleProducts[n] : null;
            own = bp ? (bp.productGID === 'default' || String(bp.productGID).split('/').pop() === String(cfg.productId)) : !$('a.kaching-bundles__bundle-products__link[href]', it);
          }
          return { sel, n, own, item: it };
        });
    },
    /* an option Kaching shows as unavailable reads "Black - Unavailable" (its value stays "Black") */
    optOut(o) { return !!o && (o.disabled || (o.value !== '' && o.textContent.trim() !== o.value)); },
    decorate() {
      const kb = state.kb;
      if (!kb) return;
      const bars = this.bars(kb);
      if (!bars.length) { this.mode(false); return; }
      this.pause();
      /* always watch again, even if a row fails half-drawn (Kaching's data still arriving): the next mutation redraws it */
      try {
        bars.forEach((bar, i) => this.bar(bar, i));
        this.addons(kb).forEach((row, n) => this.addon(row, n));
        kb.classList.add('gvb-kd');
      } catch (e) { console.error('[gvb-pdp] Kaching tiers', e); } finally { this.resume(); }
      /* bundle mode only for the deal the tier blocks describe: another deal on this product (a store-wide offer) is
         decorated as before, but the picker, the cross-sell cards and the form stay the page's own */
      this.mode(!tiers.length || bars.some((b) => b._gvb && b._gvb.cfg));
      this.read();
    },
    bar(bar, i) {
      const c = this.cfgFor(bar, i);
      const main = $('.kaching-bundles__bar-main', bar);
      if (!main) return;
      const kTitle = ($('.kaching-bundles__bar-title', bar)?.textContent || '').replace(/\s+/g, ' ').trim();
      const db = this.dbar(bar), dl = this.deal();
      /* "show both prices" prints the bar's exact total in a second pricing row (per item first): take that total */
      const tot = dl && dl.showBothPrices ? $('.kaching-bundles__bar-pricing-row--secondary .kaching-bundles__bar-price', bar) : null;
      let price = parsePrice((tot || $('.kaching-bundles__bar-price', bar))?.textContent);
      /* per-item prices (Kaching admin "show prices per item", or "show both prices" without that total row): the row
         shows the bar's total, rounded per item by Kaching, so no saving is claimed from it */
      const flagged = !!(dl && (dl.showPricesPerItem || (dl.showBothPrices && !tot && this.perItem(db) && (this.qty(db) || 1) > 1)));
      if (price != null && !tot && this.perItem(db)) price *= this.qty(db) || 1;
      const units = this.units(bar);
      /* a tier with picks of another product (Grey Cover Duo, Everyday Look Kit) gets labelled pickers per item; the
         page product's own units (1 pen, 2 pens, Buy 2 Get 1) keep the shade palette */
      const mixed = units.some((u) => !u.own);
      const own = units.filter((u) => u.own);
      const selects = mixed ? own.map((u) => u.sel) : units.map((u) => u.sel);
      const vals = selects.map((s) => s.value);
      let thumbs = '';
      if (c && c.shadeThumbs) thumbs = this.dots(vals.length ? vals : [Object.keys(shades)[0]], 3);
      else if (c && c.n) thumbs = c.el.innerHTML;
      let sub = c ? c.sub : '', subLong = c ? (c.subLong || c.sub) : '';
      if (c && c.shadeThumbs && selects.length === 1 && !mixed) { sub = subLong = this.shadeOf(vals[0]).name; }
      const name = (c && c.name) || kTitle;
      const per = c ? c.per : '';
      /* a saving only from the tier's own reference price (gvb_tier "Separate price"), never Kaching's compare-at maths,
         and never more than the items cost separately today (a component on sale lowers it; unknown = no saving) */
      const live = c && c.was && !flagged ? this.sep(bar, c) : null;
      const ref = live != null && isFinite(live) ? Math.min(c.was, live) : 0;
      const save = ref && price != null && ref > price ? ref - price : 0;
      const html = thumbs
        + '<span class="tier-text"><span class="tier-name">' + esc(name) + '</span>'
        + (sub ? '<span class="tier-sub"><span class="sub-m">' + esc(sub) + '</span><span class="sub-d">' + esc(subLong) + '</span></span>' : '')
        + (save ? '<span class="tier-save">You save <b class="num">' + G.money(save) + '</b></span>' : '') + '</span>'
        + '<span class="tier-price">' + (save ? '<s class="tier-was num"><span class="visually-hidden">Separately </span>' + G.money(ref) + '</s>' : '') + '<b class="num">' + (price != null ? G.money(price) : '') + '</b>' + (per ? '<span>' + esc(per) + '</span>' : '') + '</span>';
      let kx = $(':scope > .gvb-kx', main);
      if (!kx) { kx = document.createElement('span'); kx.className = 'gvb-kx'; main.appendChild(kx); }
      if (kx._h !== html) { kx.innerHTML = html; kx._h = html; }
      bar._gvb = { cfg: c, name, price, per, thumbs, index: i, vals, selects, kTitle, units, mixed, flagged };
      /* edge tag */
      let tag = $(':scope > .tier-tag', bar);
      if (c && c.tag) {
        if (!tag) { tag = document.createElement('em'); tag.className = 'tier-tag'; bar.appendChild(tag); }
        if (tag.textContent !== c.tag) tag.textContent = c.tag;
      } else if (tag) tag.remove();
      /* per-unit shade picks, or one labelled picker per bundle item */
      if (mixed) this.items(bar, c, units);
      else if (selects.length) this.more(bar, c, selects, vals);
      else { const m = $(':scope > .tier-more', bar); if (m) m._k = ''; } /* Kaching draws new selects when it is chosen again */
    },
    more(bar, c, selects, vals) {
      let more = $(':scope > .tier-more', bar);
      if (!more) { more = document.createElement('div'); more.className = 'tier-more'; more._act = 0; bar.appendChild(more); }
      const act = Math.min(more._act || 0, selects.length - 1);
      /* the values Kaching offers, and which of them cannot be bought: sold out in the shades JSON, or shown by Kaching
         as "Coffee - Unavailable" (a value the shades JSON leaves out when the picker hides sold-out values) */
      const ops = Array.from(selects[act].options).filter((o) => o.value);
      const opts = ops.map((o) => o.value);
      const outs = ops.map((o) => this.shadeOf(o.value).available === false || this.optOut(o));
      const unit = (c && c.unit) || 'Item';
      const m = /buy\s+(\d+)/i.exec(bar._gvb.kTitle || '');
      const paid = m ? Number(m[1]) : selects.length;
      const key = vals.join('|') + '#' + act + '#' + opts.map((v, i) => v + (outs[i] ? '!' : '')).join('|');
      if (more._k === key) return;
      const hadFocus = more.contains(document.activeElement) ? (document.activeElement.dataset.i || document.activeElement.dataset.j) : null;
      const focusKind = hadFocus != null ? (document.activeElement.classList.contains('slot') ? 'slot' : 'sw') : null;
      let h = '';
      if (selects.length > 1) {
        h += '<div class="slots slots--' + selects.length + '" role="group" aria-label="' + esc(unit) + 's in this set">';
        vals.forEach((v, j) => {
          const free = j >= paid, s = this.shadeOf(v);
          h += '<button type="button" class="slot" aria-pressed="' + (j === act) + '" data-j="' + j + '" aria-label="' + esc(unit + ' ' + (j + 1) + (free ? ' (free)' : '') + ': ' + s.name) + '">'
            + '<span class="slot-dot"><i style="--c:' + esc(s.c) + '"></i></span><span class="slot-n' + (free ? ' is-free' : '') + '">' + (free ? 'Free' : (j + 1)) + '</span></button>';
        });
        h += '</div><p class="slot-head">' + esc(unit) + ' ' + (act + 1) + ' of ' + selects.length + (act >= paid ? ' <span class="free">(free)</span>' : '') + ' · <b>' + esc(this.shadeOf(vals[act]).name) + '</b></p>';
      }
      h += '<div class="swatches pal" role="radiogroup" aria-label="' + esc(selects.length > 1 ? 'Shade for ' + unit.toLowerCase() + ' ' + (act + 1) : 'Shade') + '">';
      opts.forEach((v, i) => {
        const s = this.shadeOf(v), on = v === vals[act], out = outs[i];
        h += '<button type="button" class="sw' + (out ? ' is-soldout' : '') + '" role="radio" aria-checked="' + on + '" tabindex="' + (on ? 0 : -1) + '" data-i="' + i + '" data-v="' + esc(v) + '"' + (out ? ' data-soldout' : '') + ' aria-label="' + esc(s.name + (out ? ', sold out' : '')) + '" style="--c:' + esc(s.c) + '"><i></i></button>';
      });
      h += '</div>';
      if (selects.length === 1) { const s0 = this.shadeOf(vals[0]), o0 = s0.available === false || this.optOut(selects[0].options[selects[0].selectedIndex]); h += '<p class="shade-line"><b>' + esc(s0.name) + '</b>' + (o0 ? '<span class="sw-out"> · Sold out</span>' : (s0.hint ? ' · ' + esc(s0.hint) : '')) + '</p>'; }
      more.innerHTML = h;
      more._k = key;
      G.radiogroup($('.pal', more), (r) => {
        const v = r.dataset.v;
        /* a sold-out value is never picked: draw the palette again on the chosen one */
        if (r.hasAttribute('data-soldout')) { more._k = ''; requestAnimationFrame(() => this.decorate()); return; }
        /* the live select: Kaching draws new ones each time the bar is chosen again (the cached palette stays) */
        const sel = ((bar._gvb && bar._gvb.selects) || selects)[more._act || 0];
        if (sel && sel.value !== v) { sel.value = v; sel.dispatchEvent(new Event('input', { bubbles: true })); sel.dispatchEvent(new Event('change', { bubbles: true })); }
        requestAnimationFrame(() => this.decorate());
      });
      $$('.slot', more).forEach((b) => b.addEventListener('click', () => { more._act = Number(b.dataset.j); more._k = ''; this.decorate(); requestAnimationFrame(() => { const x = $('.slot[data-j="' + b.dataset.j + '"]', more); if (x) x.focus(); }); }));
      if (focusKind) { const x = focusKind === 'slot' ? $('.slot[data-j="' + hadFocus + '"]', more) : $('.pal [aria-checked="true"]', more); if (x) x.focus(); }
    },
    /* one labelled B dropdown per bundle item with a choice, in Kaching's item order: the label comes from the tier's
       "Item labels" (Concealer|Eyeliner|Lipstick), else the item's own title; the options are that product's own values
       (Kaching's select), never the page product's palette. A pick drives Kaching's select, so the cart follows */
    items(bar, c, units) {
      let more = $(':scope > .tier-more', bar);
      if (!more) { more = document.createElement('div'); more.className = 'tier-more'; bar.appendChild(more); }
      more.classList.add('tier-more--items');
      const rows = units.map((u, j) => {
        const opts = Array.from(u.sel.options).filter((o) => o.value !== '');
        const first = !u.item || units.findIndex((x) => x.item === u.item) === j;
        const own = (u.item && $('.kaching-bundles__bundle-products__title span:last-child', u.item)?.textContent.trim()) || u.sel.getAttribute('aria-label') || 'Item';
        let label = (c && c.labels[u.n] ? c.labels[u.n] : '') || own;
        if (!first) label += ' · ' + (u.sel.getAttribute('aria-label') || '');
        return { u, j, label, opts, v: u.sel.value };
      });
      const key = rows.map((r) => r.label + '=' + r.v + ':' + r.opts.map((o) => o.value + (this.optOut(o) || (r.u.own && this.shadeOf(o.value).available === false) ? '!' : '')).join(',')).join('|');
      if (more._k === key) return;
      const hadFocus = more.contains(document.activeElement) ? document.activeElement.dataset.j : null;
      const id = 'gvb-kp-' + (bar.dataset.dealBarId || bar._gvb.index) + '-';
      let h = '';
      rows.forEach((r) => {
        h += '<div class="kpick"><label class="kpick-l" for="' + esc(id + r.j) + '">' + esc(r.label) + '</label><span class="kpick-f"><select class="kpick-s" id="' + esc(id + r.j) + '" data-j="' + r.j + '">';
        r.opts.forEach((o) => {
          const out = this.optOut(o) || (r.u.own && this.shadeOf(o.value).available === false), nm = r.u.own ? this.shadeOf(o.value).name : o.value;
          h += '<option value="' + esc(o.value) + '"' + (o.value === r.v ? ' selected' : '') + (out ? ' disabled' : '') + '>' + esc(nm + (out ? ' · Sold out' : '')) + '</option>';
        });
        h += '</select></span></div>';
      });
      more.innerHTML = h;
      more._k = key;
      $$('select.kpick-s', more).forEach((s) => s.addEventListener('change', () => {
        /* the live select: Kaching draws new ones each time the bar is chosen again (the cached dropdowns stay) */
        const u = ((bar._gvb && bar._gvb.units) || units)[Number(s.dataset.j)];
        if (u && u.sel.value !== s.value) { u.sel.value = s.value; u.sel.dispatchEvent(new Event('input', { bubbles: true })); u.sel.dispatchEvent(new Event('change', { bubbles: true })); }
        requestAnimationFrame(() => this.decorate());
      }));
      if (hadFocus != null) { const x = $('select.kpick-s[data-j="' + hadFocus + '"]', more); if (x) x.focus(); }
    },
    /* a checkbox add-on as a B tick row: Kaching's own row (role=checkbox, its click toggles it) with thumb, title and
       ₹ price; a price in the Kaching title ("+ Slant Tweezer ₹99") is dropped, the price cell says it once */
    addon(row, n) {
      const main = $('.kaching-bundles__bar-main', row);
      if (!main) return;
      const kTitle = ($('.kaching-bundles__bar-title', row)?.textContent || '').replace(/\s+/g, ' ').trim();
      const name = kTitle.replace(/\s*(?:₹|rs\.?)\s*[\d,]+(?:\.\d+)?\s*$/i, '');
      const price = parsePrice($('.kaching-bundles__bar-price', row)?.textContent);
      const img = $('img.kaching-bundles__checkbox-upsells__image', row);
      const src = img && img.getAttribute('src');
      /* the gvb_kaching block's own picture for add-on N (Kaching's is the product's featured image, which can be a size
         chart or carry baked-in text), else Kaching's */
      const own = $('template[data-gvb-addon-thumb][data-index="' + n + '"]', root);
      const th = own ? own.innerHTML.trim() : '';
      const html = '<span class="ka-box" aria-hidden="true"></span>'
        + (th ? '<span class="tier-thumbs n1" aria-hidden="true">' + th + '</span>' : src ? '<span class="tier-thumbs n1" aria-hidden="true"><span class="tth mult"><img src="' + esc(src) + '" alt="" width="34" height="34" loading="lazy" decoding="async"></span></span>' : '<span></span>')
        + '<span class="tier-text"><span class="tier-name">' + esc(name) + '</span></span>'
        + '<span class="tier-price"><b class="num">' + (price != null ? G.money(price) : '') + '</b></span>';
      let kx = $(':scope > .gvb-kx', main);
      if (!kx) { kx = document.createElement('span'); kx.className = 'gvb-kx gvb-ka'; main.appendChild(kx); }
      if (kx._h !== html) { kx.innerHTML = html; kx._h = html; }
      row._gvb = { addon: true, name, price };
    },
    ticked(row) { const m = $('.kaching-bundles__bar-main', row); return m ? m.getAttribute('aria-checked') === 'true' : row.classList.contains('kaching-bundles__bar--selected'); },
    /* the page product's variant the chosen tier adds (Fastrr Buy now buys the form's one variant): its own shade
       select, else the tier's "Variant" setting (locked variants), else Kaching's own main cart line */
    mainVariant(bar, d) {
      const own = (d.units || []).filter((u) => u.own);
      if (own.length === 1 && shades[own[0].sel.value]) return shades[own[0].sel.value].id;
      if (d.cfg && d.cfg.variantId) return d.cfg.variantId;
      try {
        const blk = state.kb && $('kaching-bundles-block', state.kb);
        const lines = blk && typeof blk.items === 'function' ? blk.items() || [] : [];
        const main = lines.find((l) => { try { const k = JSON.parse((l.properties || {}).__kaching_bundles || '{}'); return k.main && !k.bxgy; } catch (e) { return false; } });
        if (main) return main.id;
      } catch (e) { /* Kaching API changed: keep the form as Kaching left it */ }
      return null;
    },
    read() {
      const kb = state.kb;
      const bars = this.bars(kb);
      const cur = this.current();
      /* no tier chosen (Kaching deselects a chosen bundle bar when its row is tapped again and the deal has no 1-unit
         quantity break): the buy box keeps the last tier, offers no Buy now and leaves the form alone, and that tier
         (else Kaching's default) is chosen again on the next frame, so Kaching's cart items match the button again */
      if (cur) { this._last = cur.dataset.dealBarId; this._reN = 0; }
      const last = this._last ? bars.find((b) => b.dataset.dealBarId === this._last) : null;
      const bar = cur || last || bars[0];
      if (!bar || !bar._gvb) return;
      if (!cur && this.on && !this._re && (this._reN || 0) < 3) {
        const dl = this.deal();
        const to = last || (dl && bars.find((b) => b.dataset.dealBarId === dl.preselectedDealBarId)) || bars[0];
        this._reN = (this._reN || 0) + 1;
        this._re = requestAnimationFrame(() => { this._re = 0; this.pick(to); });
      }
      const d = bar._gvb, c = d.cfg;
      /* ticked add-ons go in the bag with the tier: their prices join Add to cart, and Buy now (one variant) hides */
      const ads = this.addons(kb).filter((r) => r._gvb && this.ticked(r));
      const add = ads.reduce((s, r) => s + (r._gvb.price || 0), 0);
      const express = !!cur && (c ? c.express : d.index === 0) && !ads.length;
      /* the picker is hidden by the bundle (not on a page whose picker shows anyway, or for a tier that offers no choice
         of the page product: Kaching's single unit then follows the page's variant, so the picker stays in charge) */
      const kpick = hidePicker && this.on && !(d.units || []).some((u) => u.own) && !(c && c.variantId);
      root.classList.toggle('bb--kpick', kpick);
      const ph = hidePicker && this.on && !kpick;
      let price = d.price;
      /* per-item prices in Kaching's rows: Add to cart takes Kaching's own total for the chosen tier (with add-ons) */
      if (cur && d.flagged) {
        try {
          const blk = $('kaching-bundles-block', kb);
          const p = blk && typeof blk.pricing === 'function' ? blk.pricing() : null;
          const t = p && p.discountedPrice ? Math.round(Number(p.discountedPrice.amount) * 100) : NaN;
          if (isFinite(t) && t > add) price = t - add;
        } catch (e) { /* Kaching API changed: the row's price stays */ }
      }
      state.kind = 'tier';
      state.tier = d.index;
      if (price != null) state.price = price;
      state.add = add;
      state.per = d.per || '';
      state.express = express;
      state.cta = (c && c.cta) || 'Add to cart';
      state.name = (c && c.shadeThumbs && d.selects.length === 1 && !d.mixed) ? this.shadeOf(d.vals[0]).name : d.name;
      state.thumbs = d.thumbs ? d.thumbs.replace('aria-hidden="true"', '') : state.thumbs;
      /* the form's variant must be the tier's own (Fastrr Buy now reads it): a single unit with a shade select as before;
         with the picker hidden by the bundle, also the hidden picker's form and any single-unit tier (locked variants) */
      const f = form(), fid = f && f.elements.id;
      let vid = null;
      if (!cur) vid = null;
      else if (!d.mixed && d.selects.length === 1 && (!cfg.hasPicker || ph)) { const s = shades[d.vals[0]]; vid = s ? s.id : null; }
      else if (express && (!cfg.hasPicker || ph)) vid = this.mainVariant(bar, d);
      if (vid && fid && (!fid.disabled || ph) && String(fid.value) !== String(vid)) fid.value = vid;
      state.soldout = this.tierOut(bar);
      paint();
      const sig = [d.index, state.price, express, add].join('|');
      if (this._sig !== sig) {
        this._sig = sig;
        document.dispatchEvent(new CustomEvent('gvb:tier-change', { detail: { title: d.name, price: state.price, express, tier: d.index } }));
      }
    },
    /* a tier is sold out when the product is, when its locked variant is, when any shade it would add is (page product:
       the shades JSON; another product: Kaching's unavailable option), or (no selects) when the chosen native variant is */
    tierOut(bar) {
      if (productOut) return true;
      bar = bar || this.current();
      const d = bar && bar._gvb;
      if (d && d.cfg && d.cfg.variantOut) return true;
      if (d && d.units && d.units.length) return d.units.some((u) => (u.own && !!(shades[u.sel.value] && shades[u.sel.value].available === false)) || this.optOut(u.sel.options[u.sel.selectedIndex]));
      return varOut;
    },
    /* bundle mode on/off: one class on the buy-box root; the widget becomes the #tiers anchor only while it shows
       (blocks that do not reserve their rows on every page keep the anchors off the empty placeholder) */
    mode(on) {
      if (this.on === on) return;
      this.on = on;
      root.classList.toggle('bb--bundle', on);
      const kb = state.kb;
      if (kb && kb.hasAttribute('data-tiers-k')) {
        if (on) { kb.id = 'tiers'; kb.setAttribute('data-tiers', ''); } else { kb.removeAttribute('id'); kb.removeAttribute('data-tiers'); }
      }
      if (hideCards.length) G.updateRails();
      if (on) return;
      root.classList.remove('bb--kpick');
      /* the deal went away: back to the native variant, as the page is without one */
      state.kind = 'variant'; state.tier = -1; state.add = 0; state.cta = ''; state.express = true; this._sig = '';
      $$('[data-atc-label]', root).forEach((el) => { el.textContent = base.label; });
      const inp = $$('input[data-option-position]:checked', root)[0];
      if (inp) fromInput(inp, false);
      else { state.price = base.price; state.per = ''; state.name = base.name; state.thumbs = base.thumbs; state.soldout = productOut || varOut; paint(); }
    },
    /* full-price cross-sell cards of products the deal already sells cheaper: marked here, hidden by CSS in bundle mode
       only; the rails get their card counts without them for that case (gvb-kaching.css) */
    mark() {
      const secs = $$('[data-gvb-sec="cross-sell"]');
      secs.forEach((sec) => $$('[data-gvb-product]', sec).forEach((card) => {
        const a = $('a[href*="/products/"]', card);
        const h = a ? new URL(a.href, location.href).pathname.split('/products/')[1].split('/')[0].toLowerCase() : '';
        if (h && hideCards.indexOf(h) > -1) card.setAttribute('data-gvb-k-hide', '');
      }));
      secs.forEach((sec) => {
        $$('.xs-rail', sec).forEach((r) => {
          if (!$(':scope > [data-gvb-k-hide]', r)) return;
          r.style.setProperty('--cards-k', Math.max(1, Math.min(4, $$(':scope > [data-gvb-product]:not([data-gvb-k-hide])', r).length)));
        });
        $$('.routine-grid', sec).forEach((g) => {
          if (!$(':scope > [data-gvb-k-hide]', g)) return;
          g.style.setProperty('--tiles-k', Math.max(1, $$(':scope > .xs:not([data-gvb-k-hide])', g).length));
          if (!$(':scope > .xs-wide:not([data-gvb-k-hide]), :scope > .xs-kit', g)) g.setAttribute('data-gvb-k-nobig', '');
        });
      });
    },
    /* ad links: ?tier=<n|slug> or #tiers[-<slug>] → the bar to select (null = none asked for or no match) */
    linked(bars) {
      const slug = (t) => norm(t).replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      let want = '';
      try { want = new URLSearchParams(location.search).get('tier') || ''; } catch (e) { want = ''; }
      const hash = (location.hash || '').toLowerCase();
      const fromHash = hash.indexOf('#tiers') === 0;
      if (!want && fromHash) want = decodeURIComponent(hash.slice(6)).replace(/^[-=:_/]+/, '').split(/[&?#]/)[0];
      want = slug(want);
      const hashBar = () => bars.find((b) => b._gvb && b._gvb.cfg && b._gvb.cfg.hash) || null;
      if (!want) return fromHash ? hashBar() : null;
      if (/^\d+$/.test(want)) return bars[Number(want) - 1] || null;
      const names = (b) => b._gvb ? [slug(b._gvb.name), slug(b._gvb.kTitle)] : [];
      const exact = bars.find((b) => names(b).indexOf(want) > -1);
      if (exact) return exact;
      if (want === 'kit' && hashBar()) return hashBar();
      const words = want.split('-');
      return bars.find((b) => names(b).some((n) => { const nw = n.split('-'); return words.every((w) => nw.indexOf(w) > -1); })) || null;
    },
    pause() { if (this.mo) this.mo.disconnect(); },
    resume() { if (this.mo && state.kb) this.mo.observe(state.kb, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'value', 'data-value', 'aria-checked'], characterData: true }); },
    watch(kb) {
      state.kb = kb;
      let raf = 0, tm = 0;
      /* next frame, or 200 ms when frames are throttled (background tab): Kaching's first rows carry ₹0 until its prices land */
      const run = () => { if (!raf && !tm) return; cancelAnimationFrame(raf); clearTimeout(tm); raf = tm = 0; this.decorate(); };
      const queue = () => { if (raf || tm) return; raf = requestAnimationFrame(run); tm = setTimeout(run, 200); };
      this.mo = new MutationObserver(queue);
      this.resume();
      kb.addEventListener('change', queue);
      /* Kaching's own signals (they bubble from <kaching-bundles-block>): a new bar, new picks, a ticked add-on */
      ['deal-bar-selected', 'variants-changed', 'items-changed'].forEach((ev) => kb.addEventListener(ev, queue));
      /* a tap on the chosen tier keeps it chosen, as a radio does: Kaching would deselect it (read() covers the rest) */
      kb.addEventListener('click', (e) => {
        const m = this.on && e.target.closest ? e.target.closest('.kaching-bundles__bar-main') : null;
        const b = m && m.closest('.kaching-bundles__bar');
        if (b && b.classList.contains('kaching-bundles__bar--selected') && this.bars(kb).indexOf(b) > -1) e.stopPropagation();
      }, true);
      this.decorate();
      const bars = this.bars(kb);
      const linked = this.linked(bars);
      if (linked) this.pick(linked);
      if ((location.hash || '').toLowerCase().indexOf('#tiers') === 0) {
        $('[data-tiers]', root)?.scrollIntoView({ block: 'center' });
      } else if (!linked) {
        const pre = bars.find((b) => b._gvb && b._gvb.cfg && b._gvb.cfg.pre);
        if (pre) this.pick(pre);
      }
      /* same page, new #tiers… anchor (no reload): select that tier too */
      window.addEventListener('hashchange', () => {
        if ((location.hash || '').toLowerCase().indexOf('#tiers') !== 0) return;
        const l = this.linked(this.bars(kb));
        if (l) this.pick(l);
        /* #tiers-duo has no element of its own: bring the tier picker into view like a bare #tiers does */
        if (location.hash.length > 6) $('[data-tiers]', root)?.scrollIntoView({ block: 'center' });
      });
    },
    pick(bar) { const i = $('input[type="radio"]', bar); if (i && !i.checked) i.click(); }
  };
  if (hideCards.length) kaching.mark();
  /* Kaching renders after load: wait for its bars inside this buy box (placeholder or its own insertion) */
  const findKb = () => { const kb = $('kaching-bundle', root); return kb && $('.kaching-bundles__bar', kb) ? kb : null; };
  /* no Kaching deal will render here: release the reserved tier height at once (Kaching printed no deal for this
     product, or marked the placeholder skipped once its data was in), else after 6 s */
  const release = () => { if (!state.kb) $$('kaching-bundle.gvb-k-res', root).forEach((h) => h.classList.add('gvb-k-none')); };
  const noDeal = () => {
    const s = $('script.kaching-bundles-deal-block-settings[data-product-id="' + cfg.productId + '"]');
    return (s && !s.textContent.trim()) || $$('kaching-bundle', root).some((h) => h.getAttribute('data-initialized') === 'skipped');
  };
  const kb0 = findKb();
  if (kb0) kaching.watch(kb0);
  else {
    /* no time limit: a deal Kaching draws late (slow app or network) still gets its tiers decorated and bundle mode,
       never Kaching's raw rows next to the picker; the watch ends once the bars are here or no deal can come */
    const wait = new MutationObserver(() => { const kb = findKb(); if (kb) { wait.disconnect(); kaching.watch(kb); } else if (noDeal()) { release(); wait.disconnect(); } });
    wait.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-initialized'] });
    if (noDeal()) release();
    window.addEventListener('kaching-bundles-initialized', () => {
      const kb = findKb();
      if (kb && !state.kb) { wait.disconnect(); kaching.watch(kb); } else if (!kb && noDeal()) release();
    });
    setTimeout(release, 6000);
  }

  /* ---------------------------------------------------------------- add to cart: toast payload, "Added" */
  document.addEventListener('submit', (e) => {
    if (e.target !== form()) return;
    if (state.kind === 'tier' && !state.express) {
      G.nextToast = { name: state.name || cfg.title, variant: '', price: state.price != null ? state.price + (state.add || 0) : state.price, thumb: cfg.image };
    }
  }, true);
  document.addEventListener('variant:add', (e) => {
    if (e.target !== form()) return;
    $$('[data-atc]', root).forEach(G.flashAdded);
    if (sticky) G.flashAdded($('[data-sticky-atc]', sticky));
  });

  /* ---------------------------------------------------------------- sticky bar (BUILD-SPEC §2.3) */
  if (sticky) {
    const cta = $('[data-block-type="buy-buttons"]', root);
    const show = (on) => {
      sticky.classList.toggle('is-on', on);
      if (on) sticky.removeAttribute('aria-hidden'); else sticky.setAttribute('aria-hidden', 'true');
      $$('button', sticky).forEach((b) => { b.tabIndex = on ? 0 : -1; });
      document.body.classList.toggle('has-sticky', on);
    };
    /* When it shows (PLAN A1): never on the first screen and never over the buy box.
       - Add to cart has scrolled up past the top of the screen → on (every product).
       - "early" (section setting gvb_sticky_early, for tall buy boxes such as the lipstick's shade tiers): also on once
         the shopper has scrolled more than 120px while Add to cart is still below the screen, but only while no part
         of the buy-box form (variant picker / Kaching tiers / Add to cart) sits in the strip the bar would cover at the
         bottom of the screen. So it never covers the tiers or the real button, and on load it is always off. */
    if (cta) {
      const early = sticky.hasAttribute('data-sticky-early');
      const zone = [cta].concat($$('[data-tiers], [data-block-type="variant-picker"], [data-block-type="gvb-kaching"], [data-block-type="line-item-property"]', root));
      const inner = $('.sticky-in', sticky) || sticky;
      let on = null, raf = 0;
      const check = () => {
        raf = 0;
        const vh = window.innerHeight;
        const c = cta.getBoundingClientRect();
        if (!c.height && !c.width) { if (on !== false) { on = false; show(false); } return; }
        let want = c.bottom < 0;
        if (!want && early && window.scrollY > 120 && c.top >= vh) {
          const band = vh - (inner.offsetHeight || 72) - 16;
          let top = c.top;
          zone.forEach((el) => { const r = el.getBoundingClientRect(); if (r.height && r.top < top) top = r.top; });
          want = top >= band; /* the whole buy-box form is still below the bar's strip */
        }
        if (want !== on) { on = want; show(want); }
      };
      const queue = () => { if (!raf) raf = requestAnimationFrame(check); };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue, { passive: true });
      if ('IntersectionObserver' in window) new IntersectionObserver(queue, { threshold: 0 }).observe(cta);
      queue();
    }
    $('[data-sticky-atc]', sticky)?.addEventListener('click', () => { const b = $('[data-atc]', root); if (b && !b.disabled) b.click(); });
    $('[data-sticky-tiers]', sticky)?.addEventListener('click', () => {
      const t = $('[data-tiers]', root) || cta;
      if (t) t.scrollIntoView({ behavior: G.reduce ? 'auto' : 'smooth', block: 'center' });
    });
  }

  /* ---------------------------------------------------------------- public API (cross-sell kit card, shade sections) */
  const api = {
    state,
    selectTier(which) {
      if (!state.kb) return false;
      const bars = kaching.bars(state.kb);
      const bar = typeof which === 'number' ? bars[which] : bars.find((b) => b._gvb && (norm(b._gvb.name) === norm(which) || norm(b._gvb.kTitle) === norm(which)));
      if (!bar) return false;
      kaching.pick(bar);
      return true;
    },
    selectVariant(id) {
      fixLeft = 0;
      /* bundle mode: a tone / shade / set chosen in a section goes to the chosen tier's own pick (the picker is hidden),
         or, for locked variants (German clipper sets), to the tier that adds that variant */
      if (state.kb && kaching.on) {
        const val = Object.keys(shades).find((k) => String(shades[k].id) === String(id));
        const bar = kaching.current();
        const u = bar && bar._gvb && (bar._gvb.units || []).find((x) => x.own);
        if (val && u && Array.from(u.sel.options).some((o) => o.value === val)) {
          if (u.sel.value !== val) { u.sel.value = val; u.sel.dispatchEvent(new Event('input', { bubbles: true })); u.sel.dispatchEvent(new Event('change', { bubbles: true })); }
          return true;
        }
        const locked = kaching.bars(state.kb).find((b) => b._gvb && b._gvb.cfg && String(b._gvb.cfg.variantId) === String(id));
        if (locked) { kaching.pick(locked); return true; }
      }
      const inp = $('input[data-option-position][data-variant-id="' + id + '"]', root);
      if (inp) { if (!inp.checked) { inp.checked = true; inp.dispatchEvent(new Event('change', { bubbles: true })); } return true; }
      if (state.kb) {
        const val = Object.keys(shades).find((k) => String(shades[k].id) === String(id));
        const bar = kaching.current();
        const sel = bar && $('select.kaching-bundles__bar-variant-select', bar);
        if (val && sel && Array.from(sel.options).some((o) => o.value === val)) { sel.value = val; sel.dispatchEvent(new Event('change', { bubbles: true })); return true; }
      }
      return false;
    }
  };
  G.pdp = api;
  /* section contract (assets/gvb-sections.js): cancelable gvb:pick-tier {title} and gvb:select-variant {productId, variantId};
     handled here → preventDefault, so the section's fallback does not act twice */
  const mineEv = (d) => !d || d.productId == null || String(d.productId) === String(root.dataset.productId);
  document.addEventListener('gvb:pick-tier', (e) => { const d = e.detail || {}; if (mineEv(d) && api.selectTier(d.tier != null ? d.tier : d.title)) e.preventDefault(); });
  document.addEventListener('gvb:select-tier', (e) => { const d = e.detail || {}; if (api.selectTier(d.tier != null ? d.tier : d.title)) e.preventDefault(); });
  document.addEventListener('gvb:select-variant', (e) => { const d = e.detail || {}; if (mineEv(d) && api.selectVariant(d.variantId != null ? d.variantId : d.id)) e.preventDefault(); });

  /* ---------------------------------------------------------------- first paint from the server-rendered selection */
  const first = $$('input[data-option-position]:checked', root)[0];
  if (first) {
    /* the first checked input is only option 1: with several options (metallic lipstick "Pack / Colors") its
       data-soldout is not the chosen variant's state, so the server button stays the truth on first paint
       (?variant=<sold-out id> from catalog ads must not get a live Add to cart / Buy now) */
    const serverOut = varOut;
    fromInput(first, false);
    if (state.kind !== 'tier' && varOut !== serverOut) { varOut = serverOut; state.soldout = productOut || varOut; paint(); }
  } else {
    state.price = Number($('[data-atc]', root) ? parsePrice($('[data-atc-price]', root)?.textContent) : null) || null;
    state.soldout = productOut || varOut;
    paint();
  }
  /* sold-out landing (?variant=<sold-out id>) → first available value, in the same task as the first paint */
  let sel0 = null;
  try { sel0 = JSON.parse($('variant-picker script[data-variant]', root)?.textContent || 'null'); } catch (e) { sel0 = null; }
  if (sel0 && sel0.available === false && !productOut && !(window.Shopify && window.Shopify.designMode)) {
    const go = () => { fixLeft = new Set($$('input[data-option-position]', root).map((x) => x.dataset.optionPosition)).size; if (!fixStep()) fixLeft = 0; };
    if (customElements.get('variant-picker')) go(); else customElements.whenDefined('variant-picker').then(go);
  }
  G.updateRails(root);
  /* the phone rail scrolls: make it reachable by keyboard (axe scrollable-region-focusable); not on the desktop grid */
  const rail = $('[data-gvb-gal] [data-rail]', root);
  const railTab = () => { if (!rail) return; if (rail.scrollWidth > rail.clientWidth + 4) rail.setAttribute('tabindex', '0'); else rail.removeAttribute('tabindex'); };
  railTab();
  window.addEventListener('resize', railTab, { passive: true });
}

boot();
document.addEventListener('shopify:section:load', boot);
